import { addDays, fromDayNumber, isoWeekday, toDayNumber, todayKST } from '../periodDates';
import { withdrawalStats } from '../academyModel';

export const CALENDAR_KINDS = [
  ['all', '전체'], ['registered', '신규 등록'], ['re_registered', '재등록'],
  ['withdrawn', '퇴원'], ['class_joined', '입반'], ['class_left', '퇴반'],
  ['transferred', '반 이동'], ['note_issue', '주요 이슈'],
];
export const CALENDAR_TYPES = CALENDAR_KINDS.slice(1).map(([key]) => key);
export const eventLabel = (type) => CALENDAR_KINDS.find(([key]) => key === type)?.[1] || '일정';

export function validDate(value) {
  const n = toDayNumber(value);
  return typeof value === 'string' && value >= '1900-01-01' && value <= '9998-12-31'
    && n != null && Number.isFinite(n) && fromDayNumber(n) === value;
}

export function validMonth(value) {
  return typeof value === 'string' && /^[1-9]\d{3}-\d{2}$/.test(value) && validDate(`${value}-01`);
}

export function shiftMonth(month, amount) {
  const [year, m] = month.split('-').map(Number);
  const index = Math.max(1900 * 12, Math.min(9998 * 12 + 11, year * 12 + m - 1 + amount));
  return `${Math.floor(index / 12)}-${String(index % 12 + 1).padStart(2, '0')}`;
}

export const ADMISSION_PERIODS = [['year', '올해'], ['3m', '3개월'], ['6m', '6개월']];

/** 올해는 1월 1일부터, 개월 수는 이번 달을 포함한다. 날짜 계산은 한국 날짜 문자열로 한다. */
export function admissionPeriodRange(value, today = todayKST()) {
  const period = ADMISSION_PERIODS.some(([key]) => key === value) ? value : 'year';
  const from = period === 'year' ? `${today.slice(0, 4)}-01-01`
    : `${shiftMonth(today.slice(0, 7), period === '3m' ? -2 : -5)}-01`;
  return { period, from, to: today, asOf: today };
}

/** 예정 인원을 포함한 증감. 누락된 칸을 0으로 보이지 않는다. */
export function flowBalance(row, groupBy) {
  const incoming = groupBy === 'none' ? ['registered_new', 'registered_again', 'scheduled_registered_new', 'scheduled_registered_again']
    : ['joined', 'scheduled_joined', ...(groupBy === 'teacher' ? ['took_over', 'scheduled_took_over'] : [])];
  const outgoing = groupBy === 'none' ? ['withdrawn', 'scheduled_withdrawn']
    : ['left_total', 'scheduled_left_total', ...(groupBy === 'teacher' ? ['handed_over', 'scheduled_handed_over'] : [])];
  const sum = (keys) => keys.every((key) => Number.isInteger(row[key])) ? keys.reduce((n, key) => n + row[key], 0) : null;
  return { incoming: sum(incoming), outgoing: sum(outgoing) };
}

/** 미래가 섞이면 오늘까지 다시 조회한 같은 기간의 행만 사용한다. */
export function confirmedFlowRow(row, groupBy, period, today = todayKST()) {
  if (!validDate(period.start) || !validDate(period.end) || period.start > today) return null;
  if (period.end <= today) return row;
  const basis = period.rateBasis;
  if (basis?.start !== period.start || basis?.end !== today) return null;
  return groupBy === 'none' ? basis.total : basis.groups?.find((group) => group.key === row.key);
}

/** 휴원 포함 중단 횟수 ÷ (기간 말 수강 + 중단 횟수). 현재 명단 수로 분모를 바꾸지 않는다. */
export function flowWithdrawalStats(row, groupBy, period, today = todayKST()) {
  const confirmed = confirmedFlowRow(row, groupBy, period, today);
  return { ...withdrawalStats(confirmed?.active_closing, confirmed?.stopped),
    stoppedPause: confirmed?.stopped_pause ?? null,
    stoppedWithdraw: confirmed?.stopped_withdraw ?? null,
    stoppedOther: confirmed?.stopped_other ?? null,
    asOf: period.end < today ? period.end : today, future: period.start > today };
}

/** 오늘의 수강 인원. 같은 반 또는 같은 담임의 여러 반에 있는 학생은 한 번만 센다. */
export function groupStudentCounts(students, groupBy) {
  const groups = new Map();
  students.filter((student) => student.state === 'enrolled' && !student.is_test && !student.is_deleted).forEach((student) => {
    student.classes.forEach((cls) => {
      const key = groupBy === 'teacher' ? cls.teacher?.staff_id : cls.class_id;
      if (key == null) return;
      const id = String(key);
      if (!groups.has(id)) groups.set(id, new Set());
      groups.get(id).add(student.student_id);
    });
  });
  return new Map([...groups].map(([key, ids]) => [key, ids.size]));
}

/** 표의 수강은 현재 명단, 비율의 분모는 서버의 기간 말 수강이다. 퇴반은 중복 제거된 stopped만 쓴다. */
export function groupFlowStats(row, groupBy, period, currentGroups, today = todayKST()) {
  const confirmed = confirmedFlowRow(row, groupBy, period, today);
  const count = (value) => Number.isInteger(value) && value >= 0 ? value : null;
  const joined = count(confirmed?.joined);
  const left = count(confirmed?.stopped);
  const current = !confirmed ? null : period.end >= today
    ? (currentGroups ? currentGroups.get(String(row.key)) ?? 0 : null)
    : count(confirmed.active_closing);
  return { current, joined, left, rate: withdrawalStats(confirmed?.active_closing, left).rate };
}

/** 전체 기간은 중단 횟수만 더한다. 수강 인원은 마지막 바구니의 active_closing을 쓴다. */
export function withdrawalPeriodSummary(buckets, from, to) {
  const periods = [...buckets].sort((a, b) => a.start.localeCompare(b.start));
  const last = periods[periods.length - 1];
  const complete = periods[0]?.start === from && last?.end === to && periods.every((period, i) =>
    validDate(period.start) && validDate(period.end) && period.start <= period.end
    && (i === 0 || addDays(periods[i - 1].end, 1) === period.start));
  const sum = (key) => complete && periods.every((p) => Number.isInteger(p.total?.[key]) && p.total[key] >= 0)
    ? periods.reduce((n, p) => n + p.total[key], 0) : null;
  return { start: from, end: to, total: {
    stopped: sum('stopped'), stopped_pause: sum('stopped_pause'), stopped_withdraw: sum('stopped_withdraw'),
    active_closing: complete ? last.total?.active_closing : null,
  } };
}

export function monthRange(month) {
  const [year, m] = month.split('-').map(Number);
  return { from: `${month}-01`, to: new Date(Date.UTC(year, m, 0)).toISOString().slice(0, 10) };
}

export function calendarState(filters = {}, today = todayKST()) {
  const month = validMonth(filters.month) ? filters.month : today.slice(0, 7);
  const day = validDate(filters.day) && filters.day.startsWith(month)
    ? filters.day : (today.startsWith(month) ? today : `${month}-01`);
  const kind = CALENDAR_KINDS.some(([key]) => key === filters.kind) ? filters.kind : 'all';
  const view = filters.view === 'month' ? 'month' : 'week';
  return { month, day, kind, view };
}

function dateCells(start, length) {
  return Array.from({ length }, (_, i) => {
    const date = addDays(start, i);
    return validMonth(date.slice(0, 7)) ? date : null;
  });
}

/** 선택 날짜가 포함된 월요일~일요일. 월·연도 경계를 넘어도 한 행이다. */
export function weekCells(day) {
  return dateCells(addDays(day, 1 - isoWeekday(day)), 7);
}

export function weekRange(day) {
  const days = weekCells(day).filter(Boolean);
  return { from: days[0], to: days[days.length - 1] };
}

/** 월간은 항상 6행이다. 달을 바꿔도 카드 높이를 유지하며 이웃 달은 날짜만 표시한다. */
export function monthCells(month) {
  const { from } = monthRange(month);
  const start = addDays(from, 1 - isoWeekday(from));
  return dateCells(start, 42);
}

export function dashboardSearch(search) {
  const source = new URLSearchParams(search);
  const next = new URLSearchParams({ stats: 'overview' });
  ['as_of', 'cal_month', 'cal_day', 'cal_kind', 'cal_view', 'school_level', 'admission_period'].forEach((key) => {
    if (source.has(key)) next.set(key, source.get(key));
  });
  return `?${next}`;
}
